import { useState, useEffect, useCallback } from 'react';
import { reviewsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, GraduationCap, Loader2, History } from 'lucide-react';
import { ReviewsList } from '../components/performance-reviews/ReviewsList';
import { DueReviewsPanel } from '../components/performance-reviews/DueReviewsPanel';
import { CreateReviewModal } from '../components/performance-reviews/CreateReviewModal';
import { ReviewDetailModal } from '../components/performance-reviews/ReviewDetailModal';
import { TrainingPanel } from '../components/performance-reviews/TrainingPanel';
import { OrgChartView } from '../components/OrgChartView';
import { formatDate } from '../lib/utils';

export default function PerformanceReviewsPage() {
  const { user, canAccess } = useAuth();
  const canCreate = canAccess('PERFORMANCE_REVIEWS', 'CREATE');
  const canApprove = canAccess('PERFORMANCE_REVIEWS', 'APPROVE');
  const canManage = canCreate || canApprove;
  const isHrManager = user?.profile_id === 'hr_manager_profile';
  const isTrainer = user?.profile_id === 'trainer_profile';
  const fullVisibility = canManage || isHrManager;

  const [tab, setTab] = useState('reviews');
  const [reviews, setReviews] = useState([]);
  const [statusFilter, setStatusFilter] = useState((canApprove && !canCreate) ? 'SUBMITTED' : 'all');
  const [loading, setLoading] = useState(true);
  const [due, setDue] = useState(null);
  const [dueLoading, setDueLoading] = useState(false);
  const [orgNodes, setOrgNodes] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  // Employees tab + "Sr Executive and above" switch + score-trend history (3 Nov 2026)
  const [employeesList, setEmployeesList] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [levelSaving, setLevelSaving] = useState(null);
  const [historyUserId, setHistoryUserId] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter === 'UPCOMING') params.upcoming = true;
      else if (statusFilter !== 'all') params.status = statusFilter;
      const res = await reviewsAPI.list(params);
      setReviews(res.data || []);
    } catch (e) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { if (tab === 'reviews') fetchReviews(); }, [tab, fetchReviews]);

  useEffect(() => {
    if (tab !== 'due' || !canManage) return;
    setDueLoading(true);
    reviewsAPI.due().then((r) => setDue(r.data)).catch(() => toast.error('Failed to load due reviews')).finally(() => setDueLoading(false));
  }, [tab, canManage]);

  useEffect(() => {
    if (tab !== 'orgchart' || !canManage) return;
    reviewsAPI.orgChart().then((r) => setOrgNodes(r.data || [])).catch(() => toast.error('Failed to load org chart'));
  }, [tab, canManage]);

  const fetchEmployees = useCallback(() => {
    setEmployeesLoading(true);
    reviewsAPI.employees().then((r) => setEmployeesList(r.data || []))
      .catch(() => toast.error('Failed to load employees')).finally(() => setEmployeesLoading(false));
  }, []);
  useEffect(() => { if (tab === 'employees' && fullVisibility) fetchEmployees(); }, [tab, fullVisibility, fetchEmployees]);

  const toggleReviewLevel = async (emp) => {
    const nextLevel = emp.review_level === 'SENIOR' ? 'STANDARD' : 'SENIOR';
    setLevelSaving(emp.id);
    try {
      await reviewsAPI.setReviewLevel(emp.id, nextLevel);
      setEmployeesList((prev) => prev.map((e) => (e.id === emp.id ? { ...e, review_level: nextLevel } : e)));
      toast.success(`${emp.name} is now ${nextLevel === 'SENIOR' ? 'Sr Executive and above' : 'Standard'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change review level');
    } finally {
      setLevelSaving(null);
    }
  };

  const openHistory = (userId) => {
    setHistoryUserId(userId);
    setHistoryLoading(true);
    reviewsAPI.employeeHistory(userId).then((r) => setHistoryData(r.data || []))
      .catch(() => toast.error('Failed to load history')).finally(() => setHistoryLoading(false));
  };

  return (
    <div className="space-y-6" data-testid="performance-reviews-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><GraduationCap className="h-6 w-6" /> Performance Reviews</h1>
          <p className="text-sm text-muted-foreground">Employee reviews, approvals and training progress.</p>
        </div>
        {canCreate && (
          <Button onClick={() => setCreateOpen(true)} data-testid="new-review-btn">
            <Plus className="h-4 w-4 mr-1" /> New Review
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="reviews" data-testid="tab-reviews">{fullVisibility ? 'All Reviews' : 'My Reviews'}</TabsTrigger>
          {canManage && <TabsTrigger value="due" data-testid="tab-due">Due Reviews</TabsTrigger>}
          {canManage && <TabsTrigger value="orgchart" data-testid="tab-orgchart">Org Chart</TabsTrigger>}
          {fullVisibility && <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>}
          {isTrainer && <TabsTrigger value="training" data-testid="tab-training">My Training</TabsTrigger>}
        </TabsList>

        <TabsContent value="reviews" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {fullVisibility && (
                <div className="mb-4 w-48">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger data-testid="status-filter-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="SUBMITTED">Submitted</SelectItem>
                      <SelectItem value="SENT_BACK">Sent Back</SelectItem>
                      <SelectItem value="APPROVED">Approved</SelectItem>
                      <SelectItem value="UPCOMING" data-testid="status-filter-upcoming">Upcoming</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <ReviewsList reviews={reviews} loading={loading} onSelect={setDetailId} />
            </CardContent>
          </Card>
        </TabsContent>

        {canManage && (
          <TabsContent value="due" className="mt-4">
            <Card><CardContent className="pt-6">
              <DueReviewsPanel data={due} loading={dueLoading} onSelectEmployee={(id) => {
                const entry = [...(due?.due || []), ...(due?.not_reviewed_yet || []), ...(due?.scheduled || [])].find((e) => e.id === id);
                if (entry?.open_review_id) setDetailId(entry.open_review_id);
                else toast.info('No open review yet — use "New Review" to start one');
              }} />
            </CardContent></Card>
          </TabsContent>
        )}

        {canManage && (
          <TabsContent value="orgchart" className="mt-4">
            <Card><CardContent className="pt-6">
              <OrgChartView nodes={orgNodes} />
            </CardContent></Card>
          </TabsContent>
        )}

        {isTrainer && (
          <TabsContent value="training" className="mt-4">
            <Card><CardContent className="pt-6">
              <TrainerAssignments onSelect={setDetailId} />
            </CardContent></Card>
          </TabsContent>
        )}

        {fullVisibility && (
          <TabsContent value="employees" className="mt-4">
            <Card><CardContent className="pt-6">
              <EmployeesTable employees={employeesList} loading={employeesLoading} savingId={levelSaving}
                onToggleLevel={toggleReviewLevel} onOpenHistory={openHistory} currentUserId={user?.id} />
            </CardContent></Card>
          </TabsContent>
        )}
      </Tabs>

      <CreateReviewModal open={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={(id) => { setCreateOpen(false); fetchReviews(); setDetailId(id); }} />
      <ReviewDetailModal reviewId={detailId} open={!!detailId} onClose={() => setDetailId(null)} onChanged={fetchReviews} />
      <EmployeeHistoryDialog userId={historyUserId} data={historyData} loading={historyLoading} onClose={() => setHistoryUserId(null)}
        employeeName={employeesList.find((e) => e.id === historyUserId)?.name} />
    </div>
  );
}

function EmployeesTable({ employees, loading, savingId, onToggleLevel, onOpenHistory, currentUserId }) {
  if (loading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;
  if (employees.length === 0) return <p className="text-sm text-muted-foreground" data-testid="employees-empty">No active employees.</p>;
  return (
    <TooltipProvider>
      <div className="overflow-x-auto" data-testid="employees-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground border-b">
              <th className="py-2 pr-4">Employee</th>
              <th className="py-2 pr-4">Role</th>
              <th className="py-2 pr-4">Manager</th>
              <th className="py-2 pr-4">Sr Executive and above</th>
              <th className="py-2 pr-4">Last Review</th>
              <th className="py-2 pr-4">Score</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((e) => (
              <tr key={e.id} className="border-b last:border-0" data-testid={`employee-row-${e.id}`}>
                <td className="py-2 pr-4 font-medium">{e.name}</td>
                <td className="py-2 pr-4">{e.crm_role_name || '—'}</td>
                <td className="py-2 pr-4">{e.reporting_to_name || '—'}</td>
                <td className="py-2 pr-4">
                  {e.id === currentUserId ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span><Switch checked={e.review_level === 'SENIOR'} disabled
                          data-testid={`employee-senior-switch-${e.id}`} /></span>
                      </TooltipTrigger>
                      <TooltipContent>You cannot change your own review level</TooltipContent>
                    </Tooltip>
                  ) : (
                    <Switch checked={e.review_level === 'SENIOR'} disabled={savingId === e.id}
                      onCheckedChange={() => onToggleLevel(e)} data-testid={`employee-senior-switch-${e.id}`} />
                  )}
                </td>
                <td className="py-2 pr-4">{e.last_review_date ? formatDate(e.last_review_date) : '—'}</td>
                <td className="py-2 pr-4">
                  {e.last_score != null ? (
                    <button className="underline decoration-dotted flex items-center gap-1" onClick={() => onOpenHistory(e.id)}
                      data-testid={`employee-score-history-${e.id}`}>
                      <History className="h-3 w-3" /> {e.last_score.toFixed(2)}
                    </button>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
}

function EmployeeHistoryDialog({ userId, data, loading, onClose, employeeName }) {
  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="employee-history-dialog">
        <DialogHeader><DialogTitle>Review History{employeeName ? ` — ${employeeName}` : ''}</DialogTitle></DialogHeader>
        {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
          <TooltipProvider>
            <div className="space-y-2">
              {data.length === 0 && <p className="text-sm text-muted-foreground">No approved reviews yet.</p>}
              {data.map((r) => (
                <div key={r.id} className="flex items-center justify-between border-b pb-2" data-testid={`employee-history-row-${r.id}`}>
                  <span className="text-sm text-muted-foreground">{formatDate(r.review_date)}</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="cursor-default">{r.overall_score != null ? r.overall_score.toFixed(2) : '—'}</Badge>
                    </TooltipTrigger>
                    <TooltipContent>Scored on {(r.criteria || []).length} criteria</TooltipContent>
                  </Tooltip>
                </div>
              ))}
            </div>
          </TooltipProvider>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TrainerAssignments({ onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    reviewsAPI.myTraining().then((r) => setItems(r.data || [])).catch(() => toast.error('Failed to load assignments')).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;
  if (items.length === 0) return <p className="text-sm text-muted-foreground" data-testid="trainer-no-assignments">No training assignments.</p>;
  return (
    <div className="space-y-2" data-testid="trainer-assignments-list">
      {items.map((it) => (
        <div key={it.id} className="border rounded-md p-3 cursor-pointer hover:bg-muted/50"
          onClick={() => onSelect(it.review_id)} data-testid={`trainer-assignment-${it.id}`}>
          <p className="font-medium text-sm">{it.item_description}</p>
          <p className="text-xs text-muted-foreground">Progress: {it.progress.replace('_', ' ')}{it.due_date ? ` · Due ${it.due_date}` : ''}</p>
        </div>
      ))}
    </div>
  );
}
